import { FC, useState } from "react";
import DataTable, { type DataColumn } from "../components/DataTable/DataTable";
import EmptyState from "../components/EmptyState/EmptyState";
import useTrackingTopUsers, {
	type TopUser
} from "../hooks/useTrackingTopUsers";
import useTrackingInactiveUsers, {
	type InactiveUser
} from "../hooks/useTrackingInactiveUsers";
import UserProfilePanel from "./UserProfilePanel";
import { type Period, formatRange } from "../utils/period";
import { slugForFilename } from "../utils/csv";
import { makeTablistKeyHandler } from "../utils/tablistKeyboard";

interface Props {
	period: Period;
	canViewUserAnalytics: boolean;
}

const UsersTab: FC<Props> = ({ period, canViewUserAnalytics }) => {
	const [sub, setSub] = useState<"top" | "inactive">("top");
	const [openUser, setOpenUser] = useState<string | null>(null);

	const top = useTrackingTopUsers(
		{ from: period.from, to: period.to, limit: 25, order: "count" },
		{ enabled: canViewUserAnalytics }
	);
	const inactive = useTrackingInactiveUsers(
		{ limit: 100 },
		{ enabled: canViewUserAnalytics }
	);

	if (!canViewUserAnalytics) {
		return (
			<EmptyState
				title="Permission required"
				description="Requires CanViewUserAnalytics permission."
				tone="warn"
			/>
		);
	}

	const topColumns: DataColumn<TopUser>[] = [
		{
			key: "name",
			header: "Name",
			render: (r) => r.full_name ?? r.user_id,
			csv: (r) => r.full_name ?? r.user_id
		},
		{
			key: "title",
			header: "Title",
			render: (r) => r.job_title ?? "—",
			csv: (r) => r.job_title ?? ""
		},
		{
			key: "team",
			header: "Region",
			render: (r) => r.org_team_level_one ?? "—",
			csv: (r) => r.org_team_level_one ?? ""
		},
		{
			key: "count",
			header: "Events",
			align: "right",
			render: (r) => r.event_count.toLocaleString(),
			csv: (r) => r.event_count
		},
		{
			key: "last",
			header: "Last active",
			align: "right",
			render: (r) => r.last_active_at?.slice(0, 10) ?? "—",
			csv: (r) => r.last_active_at ?? ""
		}
	];

	const inactiveColumns: DataColumn<InactiveUser>[] = [
		{
			key: "name",
			header: "Name",
			render: (r) => r.full_name ?? r.user_id,
			csv: (r) => r.full_name ?? r.user_id
		},
		{
			key: "title",
			header: "Title",
			render: (r) => r.job_title ?? "—",
			csv: (r) => r.job_title ?? ""
		},
		{
			key: "team",
			header: "Region",
			render: (r) => r.org_team_level_one ?? "—",
			csv: (r) => r.org_team_level_one ?? ""
		},
		{
			key: "lifetime",
			header: "Lifetime events",
			align: "right",
			render: (r) => r.lifetime_event_count.toLocaleString(),
			csv: (r) => r.lifetime_event_count
		},
		{
			key: "last",
			header: "Last active",
			align: "right",
			render: (r) => r.last_active_at?.slice(0, 10) ?? "Never",
			csv: (r) => r.last_active_at ?? "Never"
		}
	];

	return (
		<div className="space-y-4">
			<div
				className="inline-flex bg-white border border-border p-1 rounded-lg shadow-xs"
				role="tablist"
				aria-label="Users sub-tab"
				onKeyDown={makeTablistKeyHandler<"top" | "inactive">(
					["top", "inactive"],
					sub,
					setSub
				)}
			>
				{(
					[
						{ key: "top", label: "Top engaged" },
						{ key: "inactive", label: "Inactive" }
					] as const
				).map((s) => (
					<button
						key={s.key}
						type="button"
						role="tab"
						aria-selected={sub === s.key}
						tabIndex={sub === s.key ? 0 : -1}
						onClick={() => setSub(s.key)}
						className={`px-3 py-1.5 text-sm rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
							sub === s.key
								? "bg-primary text-white"
								: "text-zinc-600 hover:text-foreground"
						}`}
					>
						{s.label}
					</button>
				))}
			</div>

			{sub === "top" ? (
				<DataTable
					rows={top.data ?? []}
					columns={topColumns}
					rowKey={(r) => r.user_id}
					loading={top.isLoading}
					csvFilename={`top-users-${slugForFilename(
						formatRange(period.from, period.to)
					)}.csv`}
					onRowClick={(r) => setOpenUser(r.user_id)}
					pagination={{ pageSize: 10, itemLabel: "users" }}
				/>
			) : (
				<>
					{inactive.data ? (
						<p className="text-xs text-muted-foreground">
							{inactive.data.total_count.toLocaleString()} users inactive since{" "}
							{inactive.data.since_threshold.slice(0, 10)}
						</p>
					) : null}
					<DataTable
						rows={inactive.data?.users ?? []}
						columns={inactiveColumns}
						rowKey={(r) => r.user_id}
						loading={inactive.isLoading}
						csvFilename={`inactive-users-${slugForFilename(
							formatRange(period.from, period.to)
						)}.csv`}
						onRowClick={(r) => setOpenUser(r.user_id)}
						pagination={{ pageSize: 10, itemLabel: "users" }}
					/>
				</>
			)}

			{openUser ? (
				<UserProfilePanel
					userId={openUser}
					period={period}
					onClose={() => setOpenUser(null)}
				/>
			) : null}
		</div>
	);
};

export default UsersTab;
